import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, List, ListItemButton, ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TeacherLayout from '../components/TeacherLayout';
import { teacherPortalAPI } from '../utils/api';
import type { ClassroomAttendance, TeacherChild, TeacherClassroom } from '../types/teacher';

const today = new Date().toISOString().slice(0, 10);

const formatDateTime = (value: string | null | undefined, locale: string) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const TeacherDashboard: React.FC = () => {
  const { t, i18n } = useTranslation('pages', { keyPrefix: 'teacherDashboard' });
  const [classrooms, setClassrooms] = useState<TeacherClassroom[]>([]);
  const [selected, setSelected] = useState<TeacherClassroom | null>(null);
  const [children, setChildren] = useState<TeacherChild[]>([]);
  const [attendance, setAttendance] = useState<ClassroomAttendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await teacherPortalAPI.getClassrooms();
        const list = response.classrooms || response.data || [];
        setClassrooms(list);
        setSelected(list[0] || null);
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || t('loadClassroomsFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  useEffect(() => {
    if (!selected) return;
    const loadDetails = async () => {
      setDetailLoading(true);
      setError(null);
      try {
        const [childrenResponse, attendanceResponse] = await Promise.all([
          teacherPortalAPI.getClassroomChildren(selected.id),
          teacherPortalAPI.getClassroomAttendance(selected.id, today),
        ]);
        setChildren(childrenResponse.children || childrenResponse.data || []);
        setAttendance(
          attendanceResponse.attendanceData
          || attendanceResponse.attendance
          || attendanceResponse.data
          || [...(attendanceResponse.PresentList || []), ...(attendanceResponse.AbsentList || [])],
        );
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || t('loadDetailsFailed'));
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetails();
  }, [selected, t]);

  const attendanceByChild = useMemo(() => {
    const byId = new Map<number, ClassroomAttendance>();
    const byName = new Map<string, ClassroomAttendance>();

    attendance.forEach((item) => {
      if (item.child_id != null) byId.set(Number(item.child_id), item);

      const name = item.name || item.child_name;
      if (name) byName.set(name.trim().toLocaleLowerCase(), item);
    });

    return { byId, byName };
  }, [attendance]);

  return (
    <TeacherLayout title={t('title')}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>{t('assignedClassrooms')}</Typography>
        <Typography color="text.secondary">{t('today', { date: today })}</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t('classrooms')}</Typography>
                <List>
                  {classrooms.map((classroom) => (
                    <ListItemButton key={classroom.id} selected={selected?.id === classroom.id} onClick={() => setSelected(classroom)}>
                      <ListItemText primary={classroom.name} secondary={t('childrenCount', { count: classroom.children_count ?? 0 })} />
                    </ListItemButton>
                  ))}
                  {classrooms.length === 0 && <Typography color="text.secondary">{t('noClassrooms')}</Typography>}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ overflow: 'hidden' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={700}>{t('attendanceTitle', { name: selected?.name || t('classroomFallback') })}</Typography>
              </Box>
              {detailLoading ? <Box sx={{ p: 4 }}><CircularProgress /></Box> : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('child')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                      <TableCell>{t('dropOff')}</TableCell>
                      <TableCell>{t('pickUp')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {children.map((child) => {
                      const row = attendanceByChild.byId.get(child.id)
                        || attendanceByChild.byName.get(child.name.trim().toLocaleLowerCase());
                      return (
                        <TableRow key={child.id}>
                          <TableCell>{child.name}</TableCell>
                          <TableCell><Chip size="small" label={row?.status || t('absent')} color={(row?.status || '').toLowerCase().includes('present') ? 'success' : 'default'} /></TableCell>
                          <TableCell>{formatDateTime(row?.drop_off_time, i18n.resolvedLanguage || i18n.language)}</TableCell>
                          <TableCell>{formatDateTime(row?.pick_up_time, i18n.resolvedLanguage || i18n.language)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {children.length === 0 && <TableRow><TableCell colSpan={4} align="center">{t('noChildren')}</TableCell></TableRow>}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </TeacherLayout>
  );
};

export default TeacherDashboard;
